import { useRef, useState } from 'react'
import { AlertTriangle, Download, Loader2, Upload } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { formatMaterialsCsv } from '@/features/materials/material-csv'
import { importMaterialsCsv, type ImportResult } from '@/features/materials/materials-api'
import { describeWriteError } from '@/features/users/permissions'
import { CategoryIndex } from '@/lib/category-ref'
import type { Material, MaterialCategory } from '@/lib/types'

/**
 * CSV export/import of the catalog, in the same format as the mobile app, so a file moves both ways.
 *
 * Export runs entirely in the browser (a Blob download) — no Storage, no Cloud Function, nothing that
 * would leave the Spark plan.
 */
export function MaterialsCsvActions({
  materials,
  categories,
  actor,
}: {
  materials: Material[]
  /** The category catalog: export writes current names, import resolves names to ids. */
  categories: MaterialCategory[]
  actor: { uid: string; label: string }
}) {
  const fileInput = useRef<HTMLInputElement>(null)
  const [isImporting, setImporting] = useState(false)
  const [result, setResult] = useState<ImportResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  const handleExport = () => {
    // The category column carries the linked category's CURRENT name (a renamed category exports
    // under its new name, which the import resolves back to the same id).
    const index = new CategoryIndex(categories)
    const csv = formatMaterialsCsv(
      materials.map((item) => ({ ...item, category: index.displayName(item.categoryId, item.category) })),
    )
    // A BOM so Excel opens Cyrillic text correctly; the parser strips it on the way back.
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    const stamp = new Date().toISOString().slice(0, 10)
    link.href = url
    link.download = `estimatepro-materials-${stamp}.csv`
    link.click()
    URL.revokeObjectURL(url)
  }

  const handleFile = async (file: File) => {
    setImporting(true)
    setError(null)
    setResult(null)
    try {
      const text = await file.text()
      setResult(await importMaterialsCsv(text, materials, categories, actor))
    } catch (caught) {
      setError(describeWriteError(caught))
    } finally {
      setImporting(false)
      // Allow re-picking the same file.
      if (fileInput.current) fileInput.current.value = ''
    }
  }

  return (
    <>
      <Button variant="outline" onClick={handleExport} disabled={materials.length === 0}>
        <Download />
        Экспорт CSV
      </Button>
      <Button
        variant="outline"
        onClick={() => fileInput.current?.click()}
        disabled={isImporting}
      >
        {isImporting ? <Loader2 className="animate-spin" /> : <Upload />}
        Импорт CSV
      </Button>
      <input
        ref={fileInput}
        type="file"
        accept=".csv,text/csv"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0]
          if (file) void handleFile(file)
        }}
      />

      {error ? (
        <Alert variant="destructive" className="w-full">
          <AlertTriangle />
          <AlertTitle>Импорт не выполнен</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      {result ? (
        <Dialog open onOpenChange={(open) => !open && setResult(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Импорт завершён</DialogTitle>
              <DialogDescription>
                Строки сопоставлялись по коду пункта затрат, поэтому повторный импорт того же файла
                обновляет записи, а не создаёт копии.
              </DialogDescription>
            </DialogHeader>
            <ul className="grid gap-1 text-sm">
              <li>Добавлено: {result.imported}</li>
              <li>Обновлено: {result.updated}</li>
              <li>Пропущено: {result.skipped}</li>
            </ul>
            <DialogFooter>
              <Button onClick={() => setResult(null)}>Понятно</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}
    </>
  )
}
